import {Box, Flex, Image, Text, useDisclosure} from '@chakra-ui/react';
import {colors} from 'components/providers';
import {DeliveryCostInfoModal} from 'components/shared';
import {useDesktop, useFormDate, useTranslation} from 'hooks';
import {FC} from 'react';
import {ICustomerAddToFavoritesRequest, ICustomerMyFavoritesResponse, IRestaurant} from 'types';
import {getDeliveryTimeString} from 'utils/helpers';

import {ProviderDetailsModal, ProviderTitle} from './components';
import {ProviderOption} from './components/provider-option';

interface IProps {
  provider: IRestaurant;
  myFavorite?: ICustomerMyFavoritesResponse;
  onEditFavorite: (data: ICustomerAddToFavoritesRequest) => void;
  isLoggedIn?: boolean;
  dynamicDeliveryPrice: number;
}

export const ProviderPreview: FC<IProps> = ({
  provider,
  myFavorite,
  onEditFavorite,
  isLoggedIn,
  dynamicDeliveryPrice,
}) => {
  const {
    slug,
    name,
    avg_delivery_time,
    has_adult_products,
    requires_age_verification,
    background_image_thumbnail_desktop,
    background_image_thumbnail_mobile,
    is_open,
    timetable,
    base_delivery_cost_data,
    id,
    loyalty_info,
  } = provider;
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {isOpen: isOpenInfo, onOpen: onOpenInfo, onClose: onCloseInfo} = useDisclosure();
  const {t} = useTranslation();
  const isDesktop = useDesktop();

  const {date, distanceDay} = useFormDate(timetable?.next_opening_datetime, 'dd.MM.yyyy');

  const {date: willOpenTime} = useFormDate(timetable?.from_datetime, 'HH:mm');
  const isNonStop = willOpenTime === timetable?.work_until;
  const subTitle = isNonStop
    ? t('NON_STOP')
    : is_open
    ? `${t('CLOSING_PROVIDER')} ${timetable?.work_until}`
    : distanceDay.isTomorrowDate
    ? `${t('WILL_OPEN_TOMORROW_PROVIDER')} ${willOpenTime ?? '9-00'}` //or willOpenTime ?? '9:00'
    : distanceDay.isToday
    ? `${t('WILL_OPEN_TODAY_PROVIDER')} ${willOpenTime}` //or willOpenTime ?? '9:00'
    : `${t('WILL_OPEN_PROVIDER')} ${date}`;

  const forRequestAddFavourite: ICustomerAddToFavoritesRequest = {
    slug,
    data: {
      name,
      id,
      has_adult_products,
      requires_age_verification,
    },
  };
  const isFavourite = myFavorite && myFavorite.slug === slug;
  const deliveryPrice = (base_delivery_cost_data?.delivery_cost ?? 0) + dynamicDeliveryPrice;
  const deliveryTime = getDeliveryTimeString(avg_delivery_time);

  const backgroundImg = isDesktop ? background_image_thumbnail_desktop : background_image_thumbnail_mobile;

  const favouriteIcon = isFavourite ? '/active-favorite-btn.svg' : isDesktop ? '/heart-white.svg' : '/heart-black.svg';

  return (
    <Box position="relative">
      <Flex
        borderRadius="2xl"
        height={['225px', '452px']}
        mb={['10px', '25px']}
        backgroundImage={backgroundImg}
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        position="relative"
        overflow="hidden"
        mt="31px"
      >
        <Box
          bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%)"
          position="absolute"
          w="100%"
          h="100%"
        />
        <Flex
          position="absolute"
          direction="column"
          left="0"
          right="0"
          top="0"
          bottom="0"
          p={['15px', '30px']}
          bg="linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%)"
        >
          <ProviderTitle subTitle={subTitle} provider={provider} t={t} />
          <Flex
            pt={['10px', '30px']}
            borderTop="1px solid white"
            borderColor="rgba(255, 255, 255, 0.5)"
            justifyContent="space-between"
            direction={['column', 'row']}
          >
            <Flex alignItems={['flex-start', 'center']} flexDirection={['column', 'row']}>
              <Flex direction={['row', 'column']}>
                <Text fontSize={['12px', '18px']} color={colors.white}>
                  {t('DELIVERY_TIME')}
                </Text>
                <Text fontSize={['12px', '18px']} ml={['8px', '0']} color={colors.white}>
                  {deliveryTime} {t('MINUTES')}
                </Text>
              </Flex>
              <Flex alignItems="center" direction={['row', 'column']} ml={['0px', '30px']}>
                {loyalty_info ? (
                  <Box bg="white" p="2px" borderRadius="md" px="10px">
                    <Text
                      ml={provider.discount ? '5px' : '0px'}
                      fontSize={['12px', '18px']}
                      color={colors.pink}
                      fontWeight="semibold"
                    >
                      {provider.loyalty_info}
                    </Text>
                  </Box>
                ) : (
                  <>
                    {deliveryPrice === 0 ? (
                      <Text w="200px" fontSize={['12px', '18px']} color={colors.white}>
                        {t('FREE')}
                      </Text>
                    ) : (
                      <Flex
                        flexDirection={['row', 'column']}
                        alignItems={['center', 'flex-start']}
                        cursor="pointer"
                        color={colors.white}
                        _hover={{
                          color: colors.textGray,
                        }}
                        onClick={() => onOpenInfo()}
                      >
                        <Text fontSize={['12px', '18px']}>{t('DELIVERY_FROM')}</Text>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Text fontSize={['12px', '18px']} ml={['8px', '0']}>
                            {deliveryPrice} {t('CURRENCY')}
                          </Text>
                          <Image w="30px" ml="5px" src="/info-icon.svg" alt="icon" />
                        </Flex>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </Flex>

            <Flex
              justifyContent={['flex-end', 'space-between']}
              alignItems="center"
              mt={['10px', '0']}
              position={['absolute', 'initial']}
              top="0"
              right="10px"
            >
              <ProviderOption icon={isDesktop ? '/info-icon.svg' : '/info-icon-black.svg'} onClick={onOpen}>
                {isDesktop && (
                  <Flex flexDirection="column">
                    <Text fontSize="16px" mr="10px" color={colors.white}>
                      {t('DETAILS_PROVIDER')}
                    </Text>
                    <Text fontSize="16px" mr="10px" color={colors.white}>
                      {name}
                    </Text>
                  </Flex>
                )}
              </ProviderOption>
              {isLoggedIn && (
                <Box ml={['15px', '30px']}>
                  <ProviderOption icon={favouriteIcon} onClick={() => onEditFavorite(forRequestAddFavourite)}>
                    {isDesktop && (
                      <Text fontSize={['12px', '16px']} color={colors.white} mr="15px">
                        {t('FAVOURITE')}
                      </Text>
                    )}
                  </ProviderOption>
                </Box>
              )}
            </Flex>
            {isOpen && (
              <ProviderDetailsModal
                provider={provider}
                isOpen={isOpen}
                onClose={onClose}
                activeTab="information"
                t={t}
              />
            )}
            {isOpenInfo && <DeliveryCostInfoModal isOpen={isOpenInfo} onClose={onCloseInfo} t={t} />}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
